import { defineStore } from 'pinia'
import { callWithMeta, createMeta, waitFor, type Meta } from '@/utils/store'
import * as Api from './api'
import { useSoftkeysStore } from '@/stores/softkeys/store'
import { usePromptsStore } from '@/stores/prompts/store'
import { useAudioFileStore } from '@/stores/audioFile/store'

type State = {
  callflow?: Api.CallFlow
  meta: Meta<{
    load: { id: string | number }
    updateCallflow: { id: string | number }
    validateCallflow: { id: string | number }
    callflow: { id: string | number }
  }>
}

const getDefaultState = (): State => ({
  callflow: undefined,
  meta: createMeta(['callflow', 'load', 'updateCallflow', 'validateCallflow']),
})

export const useCallflowStore = defineStore('callflow', {
  state: getDefaultState,

  actions: {
    RESET() {
      this.callflow = getDefaultState().callflow
      this.meta.callflow = getDefaultState().meta.callflow
    },

    async load(id: string | number, { useCache = true } = {}) {
      const hasCached =
        ['pending', 'success'].includes(this.meta.callflow.status) &&
        Number(id) === Number(this.meta.callflow.params?.id)

      if (useCache && hasCached) {
        await waitFor(() => this.meta.callflow.status !== 'pending')

        return this.callflow
      }

      const params = { id }

      const { result } = await callWithMeta({
        store: this,
        metaFields: { load: params, callflow: params },
        apiCall: async () => await Api.loadCallflow(id),
        onSuccess: ({ isLatest, result }) => {
          if (isLatest('callflow')) {
            this.callflow = result.payload
          }
        },
      })

      if (result.status === 'success') {
        return result.payload
      }

      throw result.payload
    },

    async reload() {
      if (
        this.meta.callflow.status !== 'initial' &&
        this.meta.callflow.params
      ) {
        await this.load(this.meta.callflow.params.id, { useCache: false })
      }
    },

    async updateCallflow({ data, id }: { data: any; id: string | number }) {
      const params = { id }

      const { result } = await callWithMeta({
        store: this,
        metaFields: { updateCallflow: params, callflow: params },
        apiCall: async () => await Api.updateCallflow(data, id, false),
        onSuccess: ({ isLatest }) => {
          if (isLatest('updateCallflow')) {
            useSoftkeysStore().RESET()

            usePromptsStore().load({ useCache: false })

            useAudioFileStore().reload()
          }

          if (isLatest('callflow')) {
            this.reload()
          }
        },
      })

      if (result.status === 'success') {
        return result.payload
      }

      throw result.payload
    },

    async validateCallflow({ data, id }: { data: any; id: string | number }) {
      const params = { id }

      const { result } = await callWithMeta({
        store: this,
        metaFields: { validateCallflow: params },
        apiCall: async () => await Api.updateCallflow(data, id, true),
      })

      if (result.status === 'success') {
        return result.payload
      }

      throw result.payload
    },
  },
})
